import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import React, { lazy, Suspense, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { setData, setGroup, setLoggedIn, setPex } from './store/userSlice.js'
import {
	setArtistIds,
	setOwnerIds,
	setArtworkIds,
} from './store/entityIdsSlice.js'
import FilterArtwork from './pages/filterartwork/index'
import AdminPanel from './pages/adminpanel/index.jsx'
import LoadingScreen from './pages/LoadingScreen.jsx'
import AdminPanelUsers from './pages/adminpanel/users/index.jsx'
import AdminPanelGroups from './pages/adminpanel/groups/index.jsx'
import AdminPanelDropdowns from './pages/adminpanel/dropdowns/index.jsx'
import Contact from './pages/contact/index.jsx'
import About from './pages/about/index.jsx'
import Home from './pages/Home/index.jsx'
import Protected from './Protected.jsx'
//global stylesheets and fonts
import './assets/util.css'
import './assets/fonts/tangerine-v17-latin-700.woff2'
import './assets/fonts/tangerine-v17-latin-regular.woff2'
import './assets/fonts/fonts.css'
import './assets/styles.css'
import Login from './pages/login/index.jsx'
import axiosInstance from './util/axios.js'
import { API_ROUTES, HUB_URL, MERCURE_TOPICS } from './util/constants.js'
import SSEClient from './util/sse.js'
import eventBus from './util/eventBus.js'

const InputMask = lazy(() => import('./pages/inputMask'))

export default function App({}) {
	const dispatch = useDispatch()
	const [loading, setLoading] = useState(true)

	function initMercureEntityIds() {
		function initEntityIds({ idsRoute, topic, setIds, entityName }) {
			return eventBus.on(topic, async (payload) => {
				if (payload.operation === 'create' || payload.operation === 'delete') {
					try {
						await fetchAndSetEntityIds({ idsRoute, setIds })
					} catch (e) {
						console.error(`Failed to refresh ${entityName} ids`, e)
					}
				}
			})
		}
		let artistCleanUp = initEntityIds({
			idsRoute: API_ROUTES.ARTIST_IDS,
			topic: MERCURE_TOPICS.ARTIST,
			setIds: setArtistIds,
			entityName: 'artist',
		})
		let ownerCleanUp = initEntityIds({
			idsRoute: API_ROUTES.OWNER_IDS,
			topic: MERCURE_TOPICS.OWNER,
			setIds: setOwnerIds,
			entityName: 'owner',
		})
		let artworkCleanUp = initEntityIds({
			idsRoute: API_ROUTES.ARTWORK_IDS,
			topic: MERCURE_TOPICS.ARTWORK,
			setIds: setArtworkIds,
			entityName: 'artwork',
		})

		return () => {
			artistCleanUp()
			ownerCleanUp()
			artworkCleanUp()
		}
	}

	async function fetchUserData() {
		let loggedIn = false
		try {
			const data = await axiosInstance.get('api/me/user')
			dispatch(setData(data))
			loggedIn = true
		} catch (e) {
			console.warn(e)
		}

		try {
			const pexdata = await axiosInstance.get('api/me/pex')
			dispatch(setPex(pexdata))
		} catch (e) {
			console.warn(e)
		}

		try {
			const group = await axiosInstance.get('api/me/group')
			dispatch(setGroup(group))
		} catch (e) {
			console.warn(e)
		}

		dispatch(setLoggedIn(loggedIn))
	}

	async function fetchAndSetEntityIds({ idsRoute, setIds }) {
		let res = null
		try {
			res = await axiosInstance.get(idsRoute)
		} catch (e) {
			// Handle permission errors gracefully
			if (e.status && (e.status === 403 || e.status === 401)) {
				console.warn('User does not have permission to read artist ids')
			} else {
				throw e
			}
		}
		const ids = res ?? []
		dispatch(setIds(ids))
	}

	async function fetchEntityIds() {
		try {
			await fetchAndSetEntityIds({
				idsRoute: API_ROUTES.ARTIST_IDS,
				topic: MERCURE_TOPICS.ARTIST,
				setIds: setArtistIds,
				entityName: 'artist',
			})

			await fetchAndSetEntityIds({
				idsRoute: API_ROUTES.OWNER_IDS,
				topic: MERCURE_TOPICS.OWNER,
				setIds: setOwnerIds,
				entityName: 'owner',
			})

			await fetchAndSetEntityIds({
				idsRoute: API_ROUTES.ARTWORK_IDS,
				topic: MERCURE_TOPICS.ARTWORK,
				setIds: setArtworkIds,
				entityName: 'artwork',
			})
		} catch (e) {
			throw e
		}
	}

	async function fetchData() {
		await fetchUserData()
			.then(() => {
				console.log('User data fetched successfully')
			})
			.catch((e) => {
				console.error('Error fetching user data:', e)
			})
		await fetchEntityIds()
			.then(() => {
				console.log('Entity IDs fetched successfully')
			})
			.catch((e) => {
				console.error('Error fetching entity IDs:', e)
			})
	}

	useEffect(() => {
		let cleanUpMercure = null
		const seeClient = new SSEClient()
		fetchData().then(() => {
			seeClient.connect()
			cleanUpMercure = initMercureEntityIds()
			setLoading(false)
		})

		return () => {
			seeClient.disconnect()
			if (cleanUpMercure) {
				cleanUpMercure()
			}
		}
	}, [])

	const router = createBrowserRouter([
		{
			path: 'login',
			element: <Login fetchData={fetchData} />,
		},
		{
			path: 'inputMask/:entityType/:entityId?',
			element: (
				<Suspense fallback={<LoadingScreen />}>
					<InputMask />
				</Suspense>
			),
		},
		{
			path: 'contact',
			element: <Contact />,
		},
		{
			path: 'about',
			element: <About />,
		},
		{
			path: 'filterartwork',
			element: (
				<Protected pexList={['read_artwork']}>
					<FilterArtwork />
				</Protected>
			),
		},
		{
			path: 'adminpanel',
			element: (
				<Protected pexList={['admin']}>
					<AdminPanel />
				</Protected>
			),
			children: [
				{
					path: 'groups',
					element: <AdminPanelGroups />,
				},
				{
					path: 'dropdowns',
					element: <AdminPanelDropdowns />,
				},
				{
					path: 'users?',
					element: <AdminPanelUsers />,
				},
			],
		},
		{
			path: '*',
			element: <Home />,
		},
	])

	return loading ? <LoadingScreen /> : <RouterProvider router={router} />
}
